import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Briefcase, X, Building2, MapPin, User, DollarSign, Sparkles, Send, AlertTriangle, Check, RefreshCw, FileText, Info, Edit2, Plus, Trash2, Paperclip, RotateCcw, Download } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useOfertaDetalhe } from '../hooks/useOfertaDetalhe';
import { useOfertas } from '../hooks/useOfertas';
import { useOptions } from '../contexts/OptionsContext';
import { useAreas } from '../hooks/useAreas';
import { useContacts } from '../hooks/useContacts';
import { useEmailsPendentes } from '../hooks/useEmailsPendentes';
import { useAttachments } from '../hooks/useAttachments';
import { useTiposDocumento } from '../hooks/useTiposDocumento';
import { useAuth } from '../hooks/useAuth';
import { colaboradores } from '../utils/stageHelpers';
import { getStageColor } from '../utils/stageHelpers';
import { DEAL_STAGES } from '../constants/dealStages';
import { formatDate } from '../utils/dateHelpers';
import { formatCurrencyCompact } from '../utils/formatHelpers';
import { EditableCell } from './EditableCell';
import { EmailTimelineItem, EmailsAmbiguosBlock } from './EmailTimelineItem';
import { WhatsAppTimelineItem } from './WhatsAppTimelineItem';
import EmailDetailDrawer from './EmailDetailDrawer';
import AttachmentBadge from './AttachmentBadge';
import UploadDialog from './UploadDialog';
import AreaImoveisPropostasSection from './imoveis/AreaImoveisPropostasSection';
import ProposalSection, { useAttachmentIdsLinkedToPropostas } from './ProposalSection';
import EntityLink from './shared/EntityLink';
import IntakeZone from './intake/IntakeZone';
import KnowledgeSection from './shared/KnowledgeSection';
import CollapsibleSection from './CollapsibleSection';
import ProximoPassoList from './ProximoPassoList';
import ResumoReviewBlock from './ResumoReviewBlock';

/**
 * Intake-1 (ADR-013) — separador visual de zona (Identidade/Inteligência/
 * Transação/Ativo/Evidência). Hierarquia leve: label discreta + linha.
 */
const ZoneLabel = ({ children }) => (
  <div className="flex items-center gap-2 text-[10px] text-slate-400 uppercase tracking-wider mt-6 mb-3 first:mt-0">
    <span className="font-semibold">{children}</span>
    <div className="flex-1 border-t border-slate-200" />
  </div>
);

const TEMPERATURA_OPTS = [
  { value: '', label: '— não definida —' },
  { value: 'frio', label: 'Frio' },
  { value: 'morno', label: 'Morno' },
  { value: 'quente', label: 'Quente' },
  { value: 'em_negociacao', label: 'Em negociação' },
];

const LADO_OPTS = [
  { value: 'interno', label: 'Interno (Aston)' },
  { value: 'incorporadora', label: 'Incorporadora' },
  { value: 'proprietaria', label: 'Proprietária' },
  { value: 'assessoria', label: 'Assessoria' },
  { value: 'outro', label: 'Outro' },
];

/**
 * BadgeEditado: indicador visual de override humano (Fix 2).
 */
const BadgeEditado = ({ visible, title }) => {
  if (!visible) return null;
  return (
    <span title={title || 'Campo editado pelo usuário (original IA preservado)'} className="inline-flex items-center gap-0.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">
      <Edit2 size={8} /> editado
    </span>
  );
};

/**
 * Editor inline do briefing IA (Fix 2).
 *
 * Storage: briefing IA vive em deal_context.metadata.briefing_completo (snapshot
 * nunca sobrescrito). Edições humanas vão em metadata.human_edits como overlay.
 * O render sempre usa human_edits[campo] ?? briefing_completo[campo].
 *
 * Regra de negócio: editar o resumo do briefing NÃO altera oferta.resumo_estagio
 * (dados distintos — briefing é IA + overlay, resumo_estagio é campo vivo da oferta).
 */
const BriefingEditor = ({ briefing, ofertaStatus, dealStages, onUpdate }) => {
  const completo = briefing?.metadata?.briefing_completo || {};
  const edits = briefing?.metadata?.human_edits || {};

  const get = (campo) => (edits[campo] !== undefined ? edits[campo] : completo[campo]);
  const isEdited = (campo) => Object.prototype.hasOwnProperty.call(edits, campo);

  const stakeholders = get('stakeholders') || [];
  const riscos = get('riscos') || [];
  const temperatura = get('temperatura') || '';
  const estagioSug = get('estagio_sugerido') || '';

  const stageOpts = [
    { value: '', label: '— sem sugestão —' },
    ...(dealStages || []).map(s => ({ value: s.value, label: s.label })),
  ];

  const updateStakeholder = (idx, campo, valor) => {
    const next = stakeholders.map((s, i) => (i === idx ? { ...s, [campo]: valor } : s));
    onUpdate('stakeholders', next);
  };
  const addStakeholder = () => onUpdate('stakeholders', [...stakeholders, { nome: '', papel: '', lado: 'interno' }]);
  const removeStakeholder = (idx) => onUpdate('stakeholders', stakeholders.filter((_, i) => i !== idx));

  const updateRisco = (idx, valor) => onUpdate('riscos', riscos.map((r, i) => (i === idx ? valor : r)));
  const addRisco = () => onUpdate('riscos', [...riscos, '']);
  const removeRisco = (idx) => onUpdate('riscos', riscos.filter((_, i) => i !== idx));

  return (
    <div className="p-3 space-y-3 text-sm">
      {/* Resumo */}
      <div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1 flex items-center gap-2">
          <span>Resumo</span>
          <BadgeEditado visible={isEdited('resumo')} />
        </div>
        <EditableCell
          value={get('resumo') || ''}
          onSave={(v) => onUpdate('resumo', v || null)}
          type="textarea"
          placeholder="Clique para adicionar resumo"
          className="text-slate-800 whitespace-pre-wrap block !text-sm"
        />
      </div>

      {/* Temperatura + Estágio sugerido (em linha) */}
      <div className="flex flex-wrap gap-3">
        <div className="min-w-[180px]">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1 flex items-center gap-2">
            <span>🌡️ Temperatura</span>
            <BadgeEditado visible={isEdited('temperatura')} />
          </div>
          <EditableCell
            value={temperatura}
            options={TEMPERATURA_OPTS}
            onSave={(v) => onUpdate('temperatura', v || null)}
            type="select"
            displayValue={TEMPERATURA_OPTS.find(o => o.value === temperatura)?.label || '— não definida —'}
            colorClass="px-2 py-0.5 rounded bg-orange-100 text-orange-800 text-[11px]"
          />
        </div>
        <div className="min-w-[220px]">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1 flex items-center gap-2">
            <span>📈 Estágio sugerido</span>
            <BadgeEditado visible={isEdited('estagio_sugerido')} />
            {estagioSug && estagioSug !== ofertaStatus && (
              <span className="text-[10px] text-blue-700" title="Diferente do estágio atual da oferta">≠ atual</span>
            )}
          </div>
          <EditableCell
            value={estagioSug}
            options={stageOpts}
            onSave={(v) => onUpdate('estagio_sugerido', v || null)}
            type="select"
            displayValue={stageOpts.find(o => o.value === estagioSug)?.label || '— sem sugestão —'}
            colorClass="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-[11px]"
          />
        </div>
      </div>

      {/* ADR-015 Fase 2: bloco de Proximo Passo deste briefing removido daqui;
          a UI agora le de proximo_passo_items (tabela colaborativa) renderizada
          no ProximoPassoList do modal. Snapshot no JSON do briefing fica preservado. */}

      {/* Stakeholders */}
      <CollapsibleSection
        title={`Stakeholders (${stakeholders.length})`}
        defaultExpanded={false}
        icon="👥"
      >
        <div className="flex items-center gap-2 mb-1">
          <BadgeEditado visible={isEdited('stakeholders')} />
          <button
            onClick={addStakeholder}
            className="ml-auto text-[10px] px-1.5 py-0.5 bg-slate-700 text-white rounded hover:bg-slate-800 flex items-center gap-0.5"
            title="Adicionar stakeholder"
          >
            <Plus size={9} /> adicionar
          </button>
        </div>
        {stakeholders.length === 0 && (
          <div className="text-xs text-slate-400 italic">Nenhum stakeholder listado.</div>
        )}
        <div className="space-y-1">
          {stakeholders.map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-1 items-center bg-white border border-slate-200 rounded p-1.5">
              <div className="col-span-4">
                <EditableCell
                  value={s.nome || ''}
                  onSave={(v) => updateStakeholder(i, 'nome', v)}
                  type="text"
                  placeholder="Nome"
                  className="text-xs font-medium"
                />
              </div>
              <div className="col-span-4">
                <EditableCell
                  value={s.papel || ''}
                  onSave={(v) => updateStakeholder(i, 'papel', v)}
                  type="text"
                  placeholder="Papel (ex: CEO, decisor técnico)"
                  className="text-xs text-slate-600"
                />
              </div>
              <div className="col-span-3">
                <EditableCell
                  value={s.lado || ''}
                  options={LADO_OPTS}
                  onSave={(v) => updateStakeholder(i, 'lado', v)}
                  type="select"
                  displayValue={LADO_OPTS.find(o => o.value === s.lado)?.label || '—'}
                  colorClass="px-1 py-0.5 bg-slate-100 rounded text-[10px]"
                />
              </div>
              <div className="col-span-1 text-right">
                <button
                  onClick={() => removeStakeholder(i)}
                  className="p-0.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                  title="Remover"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* Riscos */}
      <CollapsibleSection
        title={`Riscos (${riscos.length})`}
        defaultExpanded={false}
        icon="⚠️"
      >
        <div className="flex items-center gap-2 mb-1">
          <BadgeEditado visible={isEdited('riscos')} />
          <button
            onClick={addRisco}
            className="ml-auto text-[10px] px-1.5 py-0.5 bg-red-700 text-white rounded hover:bg-red-800 flex items-center gap-0.5"
            title="Adicionar risco"
          >
            <Plus size={9} /> adicionar
          </button>
        </div>
        {riscos.length === 0 && (
          <div className="text-xs text-slate-400 italic">Nenhum risco listado.</div>
        )}
        <div className="space-y-1">
          {riscos.map((r, i) => (
            <div key={i} className="flex items-start gap-1 bg-white border border-slate-200 rounded p-1.5">
              <span className="text-red-500 text-xs flex-shrink-0 mt-0.5">•</span>
              <div className="flex-grow min-w-0">
                <EditableCell
                  value={r || ''}
                  onSave={(v) => updateRisco(i, v)}
                  type="textarea"
                  placeholder="Descreva o risco"
                  className="text-xs text-slate-700 block"
                />
              </div>
              <button
                onClick={() => removeRisco(i)}
                className="p-0.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0"
                title="Remover"
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {briefing.metadata?.human_edits?._updated_by && (
        <div className="pt-1 text-[10px] text-amber-700 italic">
          ✏️ Editado por {briefing.metadata.human_edits._updated_by}
          {briefing.metadata.human_edits._updated_at && (
            <> em {new Date(briefing.metadata.human_edits._updated_at).toLocaleString('pt-BR')}</>
          )}
        </div>
      )}

      {briefing.metadata?.camadas_contexto && (
        <div className="pt-2 border-t border-black/5 text-[10px] text-slate-500">
          Contexto consumido: Global {briefing.metadata.camadas_contexto.global_chars || 0}c · Empresa {briefing.metadata.camadas_contexto.empresa_chars || 0}c · {briefing.metadata.camadas_contexto.emails_count || 0} emails
        </div>
      )}
    </div>
  );
};

/**
 * Modal de detalhe de uma oferta — v3 (Sessão 3.5a).
 *
 * Mudanças da v3:
 * - Emails filtrados por oferta_id (não mais por empresa_id) — corrige mistura entre ofertas
 * - Aviso visual em briefings com flag revisar_apos_classificacao + botão "Marcar como revisado"
 * - Bloco "Emails ambíguos" no final da timeline com vincular 1-clique
 * - Dropdown "Mover para outra oferta" em cada email (HITL)
 */
const OfertaDetailModal = ({ ofertaId, onClose }) => {
  const { user } = useAuth();
  const { ofertas } = useOfertas();
  const { areas } = useAreas();
  const { contatos } = useContacts();
  const { aplicarVinculosCompletos, marcarComoIgnorado, marcarComoLixo } = useEmailsPendentes();
  const {
    oferta,
    emails,
    emailsAmbiguos,
    timelineItems,
    briefingAtual,
    loading,
    error,
    adicionarNota,
    editarNota,
    vincularEmailAOferta,
    marcarBriefingRevisado,
    atualizarHumanEditBriefing,
    gerarBriefing,
    refetch,
  } = useOfertaDetalhe(ofertaId);
  const { dealStages } = useOptions();
  const { updateOferta } = useOfertas();
  const { tipos: tiposDocumento } = useTiposDocumento();
  const tiposDocumentoMap = useMemo(
    () => new Map((tiposDocumento || []).map(t => [t.value, t.label_pt])),
    [tiposDocumento]
  );

  // Fix Onda O1.1 Bloco 3 — anexos da oferta + anexos dos emails da oferta
  const emailIds = useMemo(() => (emails || []).map(e => e.id).filter(Boolean), [emails]);
  const {
    attachments,
    uploadFile: uploadAttachment,
    downloadAttachment,
    deleteAttachment,
    refetch: refetchAttachments,
  } = useAttachments(ofertaId, emailIds);

  // Separa anexos diretos da oferta (manual/drag_drop) dos anexos de cada email
  const attachmentsByEmailId = useMemo(() => {
    const map = new Map();
    for (const a of attachments) {
      if (a.email_id) {
        if (!map.has(a.email_id)) map.set(a.email_id, []);
        map.get(a.email_id).push(a);
      }
    }
    return map;
  }, [attachments]);
  const ofertaLevelAttachments = useMemo(
    () => attachments.filter(a => !a.email_id),
    [attachments]
  );

  // Intake-1 (ADR-013) — documentos por zona via document_links polimórfico.
  //   Zona 3 (Transação): docs entity_type='proposta' (renderizados dentro de ProposalSection)
  //   Zona 4 (Ativo): docs entity_type='area' + outros entity_type='oferta'
  //                   que NÃO estão atrelados a nenhuma proposta (filtro abaixo).
  const [docLinks, setDocLinks] = useState([]);          // entity_type='oferta' (todos)
  const [assetDocs, setAssetDocs] = useState([]);         // entity_type='area' (quando há area)
  const [refreshDocsKey, setRefreshDocsKey] = useState(0);

  const { proposalAttachmentIds, refetch: refetchProposalIds } =
    useAttachmentIdsLinkedToPropostas(ofertaId);

  const fetchDocLinks = useCallback(async () => {
    if (!ofertaId) {
      setDocLinks([]);
      return;
    }
    try {
      const { data, error: dlErr } = await supabase
        .from('v_entity_documents')
        .select('*')
        .eq('entity_type', 'oferta')
        .eq('entity_id', ofertaId)
        .order('created_at', { ascending: false });
      if (dlErr) throw dlErr;
      setDocLinks(data || []);
    } catch (err) {
      console.error('[OfertaDetailModal] fetchDocLinks falhou:', err);
    }
  }, [ofertaId]);

  useEffect(() => { fetchDocLinks(); }, [fetchDocLinks]);

  const areaIdForDocs = oferta?.area_id || null;
  useEffect(() => {
    if (!areaIdForDocs) { setAssetDocs([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data, error: dlErr } = await supabase
          .from('v_entity_documents')
          .select('*')
          .eq('entity_type', 'area')
          .eq('entity_id', areaIdForDocs)
          .order('created_at', { ascending: false });
        if (dlErr) throw dlErr;
        if (!cancelled) setAssetDocs(data || []);
      } catch (err) {
        console.error('[OfertaDetailModal] fetchAssetDocs falhou:', err);
        if (!cancelled) setAssetDocs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [areaIdForDocs, refreshDocsKey]);

  // Outros docs da oferta = entity_type='oferta' menos os já listados em alguma proposta.
  const otherOfertaDocs = useMemo(
    () => docLinks.filter(d => !proposalAttachmentIds.has(d.attachment_id)),
    [docLinks, proposalAttachmentIds]
  );

  const [novaNota, setNovaNota] = useState('');
  const [salvandoNota, setSalvandoNota] = useState(false);
  const [gerandoBriefing, setGerandoBriefing] = useState(false);
  const [briefingExpanded, setBriefingExpanded] = useState(true);
  const [actionError, setActionError] = useState(null);
  // Bloco 3: drag-drop + upload de anexos
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);
  // Context dialog (Onda O1.1 fix): fila de arquivos pendentes + indice atual
  // O dialog pergunta tipo + descricao antes de cada upload.
  const [pendingFiles, setPendingFiles] = useState([]); // Array<{ file, source }>
  const [pendingIdx, setPendingIdx] = useState(0);
  const [sendingFromDialog, setSendingFromDialog] = useState(false);
  // Fix 4c: drawer de detalhe de email reutilizado do Inbox de Aprendizado
  const [emailDrawerSelecionado, setEmailDrawerSelecionado] = useState(null);
  // Fix 3: edição inline de nota na timeline
  const [editandoNotaId, setEditandoNotaId] = useState(null);
  const [editNotaTexto, setEditNotaTexto] = useState('');
  const [salvandoEdicaoNota, setSalvandoEdicaoNota] = useState(false);

  // Ofertas da mesma empresa para "mover email" — exclui a atual
  const ofertasMesmaEmpresa = (ofertas || []).filter(
    o => o.empresa_id === oferta?.empresa_id && o.id !== ofertaId
  );

  // Lista de empresas derivada dos contatos (mesmo padrão do EmailsPendentesModal)
  const empresasParaDrawer = useMemo(() => {
    const map = new Map();
    (contatos || []).forEach(c => {
      if (c.empresas?.id && !map.has(c.empresas.id)) {
        map.set(c.empresas.id, c.empresas);
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.empresa || '').localeCompare(b.empresa || '')
    );
  }, [contatos]);

  const handleAbrirDetalheEmail = (emailLike) => {
    // timelineItems traz apenas um subset — pega o email completo de `emails`
    // (fetch direto já inclui to_emails/cc_emails/etc) para o drawer.
    const completo = (emails || []).find(e => e.id === emailLike.id) || emailLike;
    setEmailDrawerSelecionado(completo);
  };

  const handleAplicarVinculosEmail = async (emailId, vinculos) => {
    await aplicarVinculosCompletos(emailId, vinculos, user?.email);
    setEmailDrawerSelecionado(null);
    await refetch();
  };

  const handleIgnorarEmail = async (emailId) => {
    await marcarComoIgnorado(emailId, user?.email);
    setEmailDrawerSelecionado(null);
    await refetch();
  };

  const handleLixoEmail = async (emailId) => {
    await marcarComoLixo(emailId, user?.email);
    setEmailDrawerSelecionado(null);
    await refetch();
  };

  const handleIniciarEdicaoNota = (notaId, textoAtual) => {
    setEditandoNotaId(notaId);
    setEditNotaTexto(textoAtual || '');
  };

  const handleCancelarEdicaoNota = () => {
    setEditandoNotaId(null);
    setEditNotaTexto('');
  };

  const handleSalvarEdicaoNota = async () => {
    if (!editandoNotaId || !editNotaTexto.trim()) return;
    setSalvandoEdicaoNota(true);
    setActionError(null);
    try {
      await editarNota(editandoNotaId, editNotaTexto.trim(), user?.email);
      setEditandoNotaId(null);
      setEditNotaTexto('');
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSalvandoEdicaoNota(false);
    }
  };

  const handleUpdateBriefingField = async (campo, novoValor) => {
    if (!briefingAtual?.id) return;
    setActionError(null);
    try {
      await atualizarHumanEditBriefing(briefingAtual.id, campo, novoValor, user?.email);
    } catch (err) {
      setActionError(err.message);
    }
  };

  // Bloco 3 + Onda O1.1 fix — fluxo: drop/file-picker -> fila -> dialog -> upload
  const enqueueFiles = (files, source) => {
    if (!files || files.length === 0) return;
    setPendingFiles(files.map(f => ({ file: f, source })));
    setPendingIdx(0);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isDragging) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) enqueueFiles(files, 'drag_drop');
  };
  const handleFileInput = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) enqueueFiles(files, 'manual_upload');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDialogConfirm = async ({ document_type, description }) => {
    const current = pendingFiles[pendingIdx];
    if (!current) return;
    setSendingFromDialog(true);
    setUploadingFiles(true);
    setActionError(null);
    try {
      await uploadAttachment(current.file, {
        source: current.source,
        oferta_id: ofertaId,
        empresa_id: oferta?.empresa_id,
        area_id: oferta?.area_id || null,
        uploaded_by: user?.email,
        document_type,
        description,
      });
      // Avanca na fila ou encerra
      if (pendingIdx + 1 < pendingFiles.length) {
        setPendingIdx(prev => prev + 1);
      } else {
        setPendingFiles([]);
        setPendingIdx(0);
        await Promise.all([refetchAttachments(), fetchDocLinks()]);
        refetchProposalIds();
        setRefreshDocsKey(k => k + 1);
      }
    } catch (err) {
      setActionError(`Upload falhou: ${err.message || err}`);
      // Mantem o dialog aberto no arquivo atual para usuario ver o erro.
    } finally {
      setSendingFromDialog(false);
      setUploadingFiles(false);
    }
  };

  const handleDialogCancel = () => {
    setPendingFiles([]);
    setPendingIdx(0);
  };

  // Bloco 4 — edição inline do header da oferta
  // Fix D (SPECS_UX_STICKY_SCROLL_RESIZE): nome derivado de "EMPRESA: ÁREA" — recomputar
  // automaticamente sempre que empresa_id ou area_id mudam para manter padrão do banco.
  const handleUpdateOfertaField = async (campo, valor) => {
    if (!ofertaId) return;
    setActionError(null);
    try {
      const patch = { [campo]: valor };
      if (campo === 'empresa_id' || campo === 'area_id') {
        const novaEmpresaId = campo === 'empresa_id' ? valor : oferta?.empresa_id;
        const novaAreaId = campo === 'area_id' ? valor : oferta?.area_id;
        const empresaObj = empresasParaDrawer.find((e) => e.id === novaEmpresaId);
        const areaObj = (areas || []).find((a) => a.id === novaAreaId);
        const empresaNome = empresaObj?.empresa
          || (novaEmpresaId === oferta?.empresa_id ? oferta?.empresas?.empresa : null);
        const areaNome = areaObj?.nome
          || (novaAreaId === oferta?.area_id ? oferta?.areas?.nome : null);
        if (empresaNome) {
          patch.nome = areaNome ? `${empresaNome}: ${areaNome}` : empresaNome;
        }
      }
      await updateOferta(ofertaId, patch);
      await refetch();
    } catch (err) {
      setActionError(`Erro ao salvar ${campo}: ${err.message}`);
      await refetch();
    }
  };

  const handleSalvarNota = async () => {
    if (!novaNota.trim()) return;
    setSalvandoNota(true);
    setActionError(null);
    try {
      await adicionarNota(novaNota.trim(), user?.email);
      setNovaNota('');
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSalvandoNota(false);
    }
  };

  const handleGerarBriefing = async () => {
    setGerandoBriefing(true);
    setActionError(null);
    try {
      await gerarBriefing();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setGerandoBriefing(false);
    }
  };

  const handleMoverEmail = async (emailId, novaOfertaId) => {
    setActionError(null);
    try {
      await vincularEmailAOferta(emailId, novaOfertaId, user?.email);
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleMarcarRevisado = async () => {
    if (!briefingAtual) return;
    setActionError(null);
    try {
      await marcarBriefingRevisado(briefingAtual.id, user?.email);
    } catch (err) {
      setActionError(err.message);
    }
  };

  if (!ofertaId) return null;

  const briefingData = briefingAtual?.metadata?.briefing_completo || null;
  const precisaRevisao = briefingAtual?.metadata?.revisar_apos_classificacao === true;
  const stageColor = getStageColor(oferta?.status) || 'bg-slate-100 text-slate-700';

  // Bloco 4 — options para os dropdowns editáveis do header
  const empresasOptsHeader = empresasParaDrawer.map(e => ({ value: e.id, label: e.empresa }));
  const areasOptsHeader = (areas || []).map(a => ({ value: a.id, label: a.nome }));
  const responsavelOptsHeader = colaboradores
    .filter(c => c.id !== 'all')
    .map(c => ({ value: c.id, label: c.nome }));
  const dealStagesOptsHeader = (dealStages || []).map(s => ({ value: s.value, label: s.label }));

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] 2xl:max-w-7xl max-h-[95vh] flex flex-col">

        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="bg-orange-100 p-2 rounded-lg flex-shrink-0">
                <Briefcase className="text-orange-600" size={20} />
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-slate-800 truncate">
                  {oferta?.nome || 'Carregando oferta...'}
                </h2>
                {oferta && (
                  <div className="mt-1 inline-block">
                    <EditableCell
                      value={oferta.status || ''}
                      options={dealStagesOptsHeader}
                      onSave={(v) => handleUpdateOfertaField('status', v)}
                      type="select"
                      displayValue={dealStagesOptsHeader.find(o => o.value === oferta.status)?.label || oferta.status}
                      colorClass={`inline-block px-2 py-0.5 rounded text-xs font-medium ${stageColor}`}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleGerarBriefing}
                disabled={gerandoBriefing || loading}
                className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm flex items-center gap-1.5"
                title="Gerar novo briefing com IA considerando contexto atual"
              >
                {gerandoBriefing ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {gerandoBriefing ? 'Gerando...' : (briefingAtual ? 'Regerar Briefing' : 'Gerar Briefing')}
              </button>
              {/* Onda 4 abr/26: descartar/reativar com governança (discarded_at/by/reason). */}
              {oferta?.status === DEAL_STAGES.DESCARTADA ? (
                <button
                  onClick={async () => {
                    // eslint-disable-next-line no-alert
                    if (!window.confirm(`Reativar "${oferta.nome}"? Status volta para ${DEAL_STAGES.IDENTIFICAR}.`)) return;
                    const who = user?.email || user?.id || null;
                    try {
                      await updateOferta(ofertaId, {
                        status: DEAL_STAGES.IDENTIFICAR,
                        reactivated_at: new Date().toISOString(),
                        reactivated_by: who,
                      });
                    } catch (err) {
                      // eslint-disable-next-line no-alert
                      alert(`Erro ao reativar: ${err.message}`);
                    }
                  }}
                  className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm flex items-center gap-1.5"
                  title="Reativar oferta descartada"
                >
                  <RotateCcw size={14} /> Reativar
                </button>
              ) : (
                <button
                  onClick={async () => {
                    // eslint-disable-next-line no-alert
                    const reason = window.prompt(`Motivo para descartar "${oferta?.nome || 'esta oferta'}" (opcional):`);
                    if (reason === null) return;
                    const who = user?.email || user?.id || null;
                    try {
                      await updateOferta(ofertaId, {
                        status: DEAL_STAGES.DESCARTADA,
                        discarded_at: new Date().toISOString(),
                        discarded_by: who,
                        discard_reason: reason || null,
                      });
                    } catch (err) {
                      // eslint-disable-next-line no-alert
                      alert(`Erro ao descartar: ${err.message}`);
                    }
                  }}
                  className="px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 text-sm flex items-center gap-1.5"
                  title="Descartar oferta"
                >
                  <Trash2 size={14} /> Descartar
                </button>
              )}
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                <X size={22} />
              </button>
            </div>
          </div>

          {/* Meta (Bloco 4 — cada campo editável inline) */}
          {oferta && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-xs text-slate-600">
              <div className="flex items-center gap-1">
                <Building2 size={11} className="text-blue-600 flex-shrink-0" />
                <EditableCell
                  value={oferta.empresa_id || ''}
                  options={empresasOptsHeader}
                  onSave={(v) => v && handleUpdateOfertaField('empresa_id', v)}
                  type="select"
                  displayValue={oferta.empresas?.empresa || '— empresa —'}
                  className="text-xs"
                />
              </div>
              {oferta.contato_negociador && (
                <div className="flex items-center gap-1" title={oferta.negociador_source ? `Fonte: ${oferta.negociador_source} (${oferta.negociador_confidence ?? '?'})` : undefined}>
                  <span className="text-slate-400">Negociador:</span>
                  <EntityLink
                    entityType="contato"
                    entityId={oferta.contato_negociador.id}
                    label={`${oferta.contato_negociador.first_name || ''} ${oferta.contato_negociador.last_name || ''}`.trim() + (oferta.contato_negociador.title ? ` (${oferta.contato_negociador.title})` : '')}
                    showIcon={false}
                  />
                  {oferta.contato_negociador.eh_decisor && <span className="text-[9px]" title="Decisor">⭐</span>}
                  {oferta.negociador_source && oferta.negociador_source !== 'human' && (
                    <span className="text-[9px] text-slate-400 italic">inferido</span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-1">
                <MapPin size={11} className="text-purple-600 flex-shrink-0" />
                <EditableCell
                  value={oferta.area_id || ''}
                  options={[{ value: '', label: '— sem área —' }, ...areasOptsHeader]}
                  onSave={(v) => handleUpdateOfertaField('area_id', v || null)}
                  type="select"
                  displayValue={oferta.areas?.nome || '— sem área —'}
                  className="text-xs"
                />
              </div>
              <div className="flex items-center gap-1">
                <User size={11} className="flex-shrink-0" />
                <EditableCell
                  value={oferta.responsavel_id || ''}
                  options={[{ value: '', label: '—' }, ...responsavelOptsHeader]}
                  onSave={(v) => handleUpdateOfertaField('responsavel_id', v || null)}
                  type="select"
                  displayValue={
                    oferta.colaboradores?.nome ||
                    responsavelOptsHeader.find(r => r.value === oferta.responsavel_id)?.label ||
                    '—'
                  }
                  className="text-xs"
                />
              </div>
              <div className="flex items-center gap-1">
                <DollarSign size={11} className="flex-shrink-0" />
                <EditableCell
                  value={oferta.valor_proposto != null ? String(oferta.valor_proposto) : ''}
                  onSave={(v) => {
                    const raw = (v || '').replace(/[^\d.,-]/g, '').replace(',', '.');
                    const num = raw ? parseFloat(raw) : null;
                    handleUpdateOfertaField('valor_proposto', Number.isFinite(num) ? num : null);
                  }}
                  type="text"
                  placeholder="—"
                  displayValue={
                    oferta.valor_proposto != null && Number(oferta.valor_proposto) > 0
                      ? formatCurrencyCompact(oferta.valor_proposto)
                      : '—'
                  }
                  className="text-xs"
                />
              </div>
            </div>
          )}
        </div>

        {/* Conteúdo */}
        <div className="flex-grow overflow-y-auto p-4">
          {loading && (
            <div className="text-center py-10 text-sm text-slate-500">Carregando detalhes da oferta...</div>
          )}

          {(error || actionError) && (
            <div className="mb-3 bg-red-50 border border-red-200 rounded p-2.5 text-xs text-red-800 flex items-start gap-1.5">
              <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
              <span>{error || actionError}</span>
            </div>
          )}

          {!loading && oferta && (
            <>
              {/* ============================================================
                  ZONA 2 — INTELIGÊNCIA (Briefing IA + Knowledge atoms)
                  ============================================================ */}
              <ZoneLabel>Inteligência</ZoneLabel>

              {/* BLOCO: Briefing IA (se existe) */}
              {briefingAtual && (
                <div className={`mb-4 rounded-lg border ${precisaRevisao ? 'border-amber-300 bg-amber-50' : 'border-purple-200 bg-purple-50/50'}`}>
                  <div className="p-3 border-b border-black/5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles size={14} className={precisaRevisao ? 'text-amber-600' : 'text-purple-600'} />
                      <span className={`text-sm font-semibold ${precisaRevisao ? 'text-amber-900' : 'text-purple-900'}`}>
                        Briefing gerado por IA
                      </span>
                      <span className="text-[10px] text-slate-500">
                        {formatDate(briefingAtual.created_at)}
                      </span>
                    </div>
                    <button
                      onClick={() => setBriefingExpanded(!briefingExpanded)}
                      className="text-xs text-slate-500 hover:text-slate-700"
                    >
                      {briefingExpanded ? 'Recolher' : 'Expandir'}
                    </button>
                  </div>

                  {/* AVISO DE REVISÃO (Sessão 3.5a) */}
                  {precisaRevisao && (
                    <div className="p-3 border-b border-amber-200 bg-amber-100 text-xs text-amber-900 flex items-start gap-2">
                      <Info size={12} className="flex-shrink-0 mt-0.5" />
                      <div className="flex-grow">
                        <strong>Atenção — briefing anterior à classificação de emails:</strong>{' '}
                        {briefingAtual.metadata?.motivo_revisao || 'Este briefing pode conter referências a emails que agora estão corretamente vinculados a outras ofertas desta mesma empresa. Recomenda-se regerar.'}
                      </div>
                      <div className="flex-shrink-0 flex gap-1">
                        <button
                          onClick={handleMarcarRevisado}
                          className="px-2 py-0.5 text-[11px] bg-white border border-amber-400 text-amber-800 rounded hover:bg-amber-50 flex items-center gap-0.5"
                        >
                          <Check size={10} /> Ignorar aviso
                        </button>
                        <button
                          onClick={handleGerarBriefing}
                          disabled={gerandoBriefing}
                          className="px-2 py-0.5 text-[11px] bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                        >
                          Regerar agora
                        </button>
                      </div>
                    </div>
                  )}

                  {briefingExpanded && briefingData && (
                    <BriefingEditor
                      briefing={briefingAtual}
                      ofertaStatus={oferta.status}
                      dealStages={dealStages}
                      onUpdate={handleUpdateBriefingField}
                    />
                  )}
                </div>
              )}

              {/* BLOCO: Resumo Review (ADR-015 Fase 3) — banner de proposta de
                  resumo do agent quando resumo_source IN ('human','merged').
                  Aparece apenas quando ha proposta pendente. */}
              <ResumoReviewBlock
                ofertaId={ofertaId}
                currentResumo={oferta?.resumo_estagio}
                userEmail={user?.email}
                onResumoUpdated={() => refetch?.()}
              />

              {/* BLOCO: Proximo Passo (ADR-015 Fase 2) — lista colaborativa
                  agent <> humano. Substitui briefing.proximo_passo (texto unico). */}
              <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50/30">
                <div className="p-3 border-b border-black/5 flex items-center gap-2">
                  <span className="text-base">🎯</span>
                  <span className="text-sm font-semibold text-orange-900">Próximo passo</span>
                  <span className="text-[10px] text-slate-500">colaborativo · agent + humano</span>
                </div>
                <div className="p-3">
                  <ProximoPassoList ofertaId={ofertaId} userEmail={user?.email} />
                </div>
              </div>

              {/* ============================================================
                  ZONA — EVIDÊNCIA (Timeline + Emails ambíguos) — movida pra cima
                  ============================================================ */}
              <ZoneLabel>Evidência</ZoneLabel>

              {/* BLOCO: Timeline (notas + emails desta oferta) — com drop zone de anexos */}
              <div
                className="mb-4"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <CollapsibleSection
                  title={`Timeline (${timelineItems.length + ofertaLevelAttachments.length} itens)`}
                  defaultExpanded={false}
                  icon="📅"
                  badge={`${emails.length} email${emails.length !== 1 ? 's' : ''} · ${attachments.length} anexo${attachments.length !== 1 ? 's' : ''}`}
                >
                <div className="flex items-center justify-end mb-2">
                  <label className="cursor-pointer px-2 py-1 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 flex items-center gap-1 text-[11px] border border-slate-300">
                    <Paperclip size={11} />
                    {uploadingFiles ? 'Enviando...' : 'Anexar'}
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileInput}
                      disabled={uploadingFiles}
                    />
                  </label>
                </div>

                {isDragging && (
                  <div className="mb-2 border-2 border-dashed border-indigo-400 bg-indigo-50 rounded-lg py-4 text-center text-sm text-indigo-700 flex items-center justify-center gap-2">
                    <Paperclip size={14} /> Solte os arquivos aqui para anexar à oferta
                  </div>
                )}

                <div className={`bg-white border border-slate-200 rounded divide-y divide-slate-100 ${isDragging ? 'ring-2 ring-indigo-300' : ''}`}>
                  {timelineItems.length === 0 && (
                    <div className="p-4 text-center text-xs text-slate-400 italic">
                      Nenhum item ainda. Use o campo abaixo para adicionar a primeira nota.
                    </div>
                  )}

                  {timelineItems.map(item => {
                    if (item.kind === 'email') {
                      const emailAttachments = attachmentsByEmailId.get(item.id) || [];
                      return (
                        <div key={`email-${item.id}`} className="px-3">
                          <EmailTimelineItem
                            email={{
                              id: item.id,
                              from_email: item.from_email,
                              to_emails: item.to_emails,
                              cc_emails: item.cc_emails,
                              subject: item.subject,
                              data_envio: item.data,
                              direcao: item.direcao,
                              lado: item.lado,
                              body_text: item.texto,
                              classificacao_status: item.classificacao_status || 'auto_classificado',
                              classificacao_confianca: item.classificacao_confianca,
                              classificado_por: item.classificado_por,
                            }}
                            ofertasDisponiveis={ofertasMesmaEmpresa}
                            ofertaAtualId={ofertaId}
                            onMoverOferta={handleMoverEmail}
                            onAbrirDetalhe={handleAbrirDetalheEmail}
                          />
                          {emailAttachments.length > 0 && (
                            <div className="pl-9 pb-2 flex flex-wrap gap-1.5">
                              {emailAttachments.map(att => (
                                <AttachmentBadge
                                  key={att.id}
                                  attachment={att}
                                  onDownload={downloadAttachment}
                                  onDelete={deleteAttachment}
                                  tiposMap={tiposDocumentoMap}
                                  compact
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }

                    if (item.kind === 'whatsapp') {
                      return (
                        <div key={`whatsapp-${item.id}`} className="px-3">
                          <WhatsAppTimelineItem message={item} />
                        </div>
                      );
                    }

                    if (item.kind === 'nota' || item.kind === 'briefing') {
                      // Nota manual ou briefing já exibido acima — mostra como item compacto
                      if (item.kind === 'briefing') return null; // Já mostrado no bloco principal
                      const emEdicao = editandoNotaId === item.id;
                      const foiEditada = Array.isArray(item.metadata?.edits) && item.metadata.edits.length > 0;
                      const isAgentNote = item.autor_id?.startsWith('agent_');
                      const notaBorderClass = isAgentNote
                        ? 'border-l-4 border-l-violet-400 bg-violet-50/40'
                        : 'border-l-4 border-l-orange-500 bg-orange-50/60';
                      const notaAvatarClass = isAgentNote
                        ? 'bg-violet-100 text-violet-600'
                        : 'bg-orange-100 text-orange-700';
                      const notaIcon = isAgentNote ? '🤖' : '📝';
                      const notaLabel = isAgentNote ? 'Nota de agente' : 'Nota do colaborador';
                      return (
                        <div key={`nota-${item.id}`} className={`px-3 py-2.5 flex gap-2 group ${notaBorderClass}`}>
                          <div className="flex-shrink-0 mt-0.5">
                            <div className={`w-7 h-7 rounded-full ${notaAvatarClass} flex items-center justify-center text-xs`}>
                              {notaIcon}
                            </div>
                          </div>
                          <div className="flex-grow min-w-0">
                            <div className="text-[11px] text-slate-500 flex items-center gap-2">
                              <span>
                                <span className={isAgentNote ? 'font-medium text-violet-700' : 'font-medium text-orange-800'}>
                                  {notaLabel}
                                </span>
                                {' '}por {item.autor_id || 'usuário'} · {formatDate(item.data)}
                              </span>
                              {foiEditada && (
                                <span
                                  className="text-amber-600 flex items-center gap-0.5"
                                  title={`Editada ${item.metadata.edits.length}x (última por ${item.metadata.editada_por || '—'})`}
                                >
                                  <Edit2 size={9} /> editada
                                </span>
                              )}
                            </div>
                            {emEdicao ? (
                              <div className="mt-1 space-y-2">
                                <textarea
                                  value={editNotaTexto}
                                  onChange={(e) => setEditNotaTexto(e.target.value)}
                                  onInput={(e) => {
                                    e.target.style.height = 'auto';
                                    e.target.style.height = Math.min(e.target.scrollHeight, window.innerHeight * 0.5) + 'px';
                                  }}
                                  ref={(el) => {
                                    if (el && el.scrollHeight && el.style.height === '') {
                                      el.style.height = Math.min(el.scrollHeight, window.innerHeight * 0.5) + 'px';
                                    }
                                  }}
                                  className="w-full border border-slate-300 rounded p-2 text-sm min-h-[80px] overflow-y-auto focus:ring-2 focus:ring-orange-500 outline-none"
                                  style={{ maxHeight: '50vh' }}
                                  autoFocus
                                  disabled={salvandoEdicaoNota}
                                />
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={handleCancelarEdicaoNota}
                                    disabled={salvandoEdicaoNota}
                                    className="px-2.5 py-1 text-xs text-slate-500 hover:bg-slate-100 rounded"
                                  >
                                    Cancelar
                                  </button>
                                  <button
                                    onClick={handleSalvarEdicaoNota}
                                    disabled={salvandoEdicaoNota || !editNotaTexto.trim()}
                                    className="px-2.5 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1"
                                  >
                                    <Check size={11} /> {salvandoEdicaoNota ? 'Salvando...' : 'Salvar'}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="relative">
                                <div className="text-sm text-slate-800 whitespace-pre-wrap mt-0.5 pr-7">
                                  {item.texto}
                                </div>
                                <button
                                  onClick={() => handleIniciarEdicaoNota(item.id, item.texto)}
                                  className="absolute top-0 right-0 p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Editar nota"
                                >
                                  <Edit2 size={12} />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }

                    return null;
                  })}

                  {/* Anexos de nível oferta (manual_upload / drag_drop) como itens próprios */}
                  {ofertaLevelAttachments.map(att => (
                    <div key={`att-${att.id}`} className="px-3 py-2.5 flex gap-2">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs">
                          📎
                        </div>
                      </div>
                      <div className="flex-grow min-w-0">
                        <div className="text-[11px] text-slate-500">
                          Anexo via {att.source === 'drag_drop' ? 'drag&drop' : 'upload'}
                          {att.uploaded_by && ` · ${att.uploaded_by}`}
                          {' · '}{formatDate(att.created_at)}
                        </div>
                        <div className="mt-1">
                          <AttachmentBadge
                            attachment={att}
                            onDownload={downloadAttachment}
                            onDelete={deleteAttachment}
                            tiposMap={tiposDocumentoMap}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </CollapsibleSection>
              </div>

              {/* BLOCO: Emails ambíguos (Sessão 3.5a) */}
              <EmailsAmbiguosBlock
                emailsAmbiguos={emailsAmbiguos}
                ofertaAtualId={ofertaId}
                onMoverOferta={handleMoverEmail}
              />

              {/* ============================================================
                  ZONA — CONHECIMENTO (knowledge_atoms via v_entity_knowledge)
                  ============================================================ */}
              <ZoneLabel>Conhecimento</ZoneLabel>
              <div className="mb-4">
                <KnowledgeSection entityType="oferta" entityId={ofertaId} />
              </div>

              {/* ============================================================
                  ZONA 3 — TRANSAÇÃO (Propostas com docs inline)
                  ============================================================ */}
              <ZoneLabel>Transação</ZoneLabel>
              <ProposalSection
                ofertaId={ofertaId}
                downloadAttachment={downloadAttachment}
                tiposDocumentoMap={tiposDocumentoMap}
                refreshKey={refreshDocsKey}
              />

              {/* ============================================================
                  ZONA 4 — ATIVO (Área + Imóveis + Proprietários + Docs do ativo)
                  ============================================================ */}
              <div className="mt-6">
              <CollapsibleSection
                title="Ativo"
                defaultExpanded={false}
                icon="🏗️"
              >
              {oferta.area_id && (
                <AreaImoveisPropostasSection
                  ofertaId={ofertaId}
                  areaId={oferta.area_id}
                  areaNome={oferta.areas?.nome}
                  areaM2={oferta.areas?.area_total_m2}
                />
              )}

              {/* Documentos do ativo: docs vinculados à área (matrícula, certidão, IPTU...) */}
              {oferta.area_id && assetDocs.length > 0 && (
                <div className="mb-4">
                  <div className="text-[11px] font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                    <Paperclip size={11} className="text-slate-500" />
                    Documentos do ativo ({assetDocs.length})
                  </div>
                  <div className="bg-white border border-slate-200 rounded divide-y divide-slate-100">
                    {assetDocs.map(d => (
                      <div key={d.attachment_id} className="px-3 py-2 flex items-center gap-2 text-xs">
                        <FileText size={13} className="text-slate-400 flex-shrink-0" />
                        <div className="min-w-0 flex-grow">
                          <div className="font-medium text-slate-800 truncate" title={d.filename || ''}>
                            {d.filename || '(sem nome)'}
                          </div>
                          <div className="text-[10px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                            {d.document_type && (
                              <span className="px-1 bg-slate-100 rounded">
                                {tiposDocumentoMap.get(d.document_type) || d.document_type}
                              </span>
                            )}
                            <span>{formatDate(d.created_at)}</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => downloadAttachment({ storage_path: d.storage_path, filename: d.filename })}
                          disabled={!d.storage_path}
                          title="Baixar"
                          className="p-1 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded disabled:opacity-30"
                        >
                          <Download size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Outros documentos da oferta — link entity_type='oferta' que NÃO
                  está atrelado a nenhuma proposta (briefing PDF, e-mail enviado, etc.) */}
              {otherOfertaDocs.length > 0 && (
                <div className="mb-4">
                  <div className="text-[11px] font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                    <Paperclip size={11} className="text-slate-500" />
                    Outros documentos da oferta ({otherOfertaDocs.length})
                  </div>
                  <div className="bg-white border border-slate-200 rounded divide-y divide-slate-100">
                    {otherOfertaDocs.map(d => (
                      <div key={d.attachment_id} className="px-3 py-2 flex items-center gap-2 text-xs">
                        <FileText size={13} className="text-slate-400 flex-shrink-0" />
                        <div className="min-w-0 flex-grow">
                          <div className="font-medium text-slate-800 truncate" title={d.filename || ''}>
                            {d.filename || '(sem nome)'}
                          </div>
                          <div className="text-[10px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                            {d.document_type && (
                              <span className="px-1 bg-slate-100 rounded">
                                {tiposDocumentoMap.get(d.document_type) || d.document_type}
                              </span>
                            )}
                            <span>{formatDate(d.created_at)}</span>
                            {d.uploaded_by && <span>· {d.uploaded_by}</span>}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => downloadAttachment({ storage_path: d.storage_path, filename: d.filename })}
                          disabled={!d.storage_path}
                          title="Baixar"
                          className="p-1 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded disabled:opacity-30"
                        >
                          <Download size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </CollapsibleSection>
              </div>

              {/* ============================================================
                  ZONA — INTAKE DE DOCUMENTO
                  ============================================================ */}
              <ZoneLabel>Intake de documento</ZoneLabel>

              {/* T5b: IntakeZone para proposta comercial */}
              <section className="mt-4">
                <IntakeZone
                  entityType="oferta"
                  entityId={ofertaId}
                  defaultDocType="proposta_comercial"
                  onComplete={refetch}
                />
              </section>
            </>
          )}
        </div>

        {/* Footer: adicionar nota */}
        <div className="p-3 border-t border-slate-200 bg-slate-50 flex-shrink-0">
          <div className="flex gap-2">
            <textarea
              value={novaNota}
              onChange={(e) => setNovaNota(e.target.value)}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(Math.max(e.target.scrollHeight, 50), window.innerHeight * 0.5) + 'px';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSalvarNota();
              }}
              placeholder="Adicionar nota ao contexto desta oferta..."
              className="flex-grow px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none overflow-y-auto min-h-[50px]"
              style={{ maxHeight: '50vh' }}
              disabled={salvandoNota}
            />
            <button
              onClick={handleSalvarNota}
              disabled={!novaNota.trim() || salvandoNota}
              className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm flex items-center gap-1"
            >
              <Send size={14} /> {salvandoNota ? 'Salvando...' : 'Adicionar'}
            </button>
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            Dica: Ctrl+Enter para salvar · Briefings ficam no histórico
          </div>
        </div>
      </div>

      {/* Fix 4c: drawer de detalhe de email (reutilizado do Inbox de Aprendizado) */}
      {emailDrawerSelecionado && (
        <EmailDetailDrawer
          email={emailDrawerSelecionado}
          empresas={empresasParaDrawer}
          areas={areas}
          ofertas={ofertas}
          onAplicarVinculos={handleAplicarVinculosEmail}
          onIgnorar={handleIgnorarEmail}
          onLixo={handleLixoEmail}
          onClose={() => setEmailDrawerSelecionado(null)}
        />
      )}

      {/* Onda O1.1 fix — dialog contextual de upload */}
      {pendingFiles.length > 0 && pendingIdx < pendingFiles.length && (
        <UploadDialog
          file={pendingFiles[pendingIdx].file}
          tipos={tiposDocumento}
          queueIndex={pendingIdx}
          queueLength={pendingFiles.length}
          sending={sendingFromDialog}
          onConfirm={handleDialogConfirm}
          onCancel={handleDialogCancel}
        />
      )}
    </div>
  );
};

export default OfertaDetailModal;
